
import { ElasticSearchService, ESExtras, ESSort } from '../api-server/elastic-search.service';
import { collection, collectionData, CollectionReference, doc, docData, DocumentData, DocumentReference, Firestore, increment, limit, query, QueryDocumentSnapshot, serverTimestamp, setDoc, SnapshotOptions, WithFieldValue } from '@angular/fire/firestore';
import { deleteDoc } from '@firebase/firestore';
import { Observable } from 'rxjs';
import { toDate } from '../helpers/utils';
import { get, set } from 'lodash';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

/**
 * Clase base para ser reutilizada en los db-services
 *  super(firestore, es, 'orders', esExtra, esSource)
 *
 * Se pueden customizar los serializadores.
 * 1.- Es se declara nuevamente esNormalize
 * 2.- Para ffirestore tenemos dosfunciones
 * this.genericConverter.toFirestore =  (data: WithFieldValue<Model>) => {....}
 * this.genericConverter.fromFirestore =  ( snapshot: QueryDocumentSnapshot, options: SnapshotOptions) => {....}
 *
 */
export interface EsFirestoreBaseOptions{
  utcDateProperties?:Array<string>
}
export abstract class EsFirestoreBase<Model> {
  public collection: CollectionReference<any>;
  // public dateProperties = ['createdAt', 'updatedAt']
  // private options;


  genericConverter = {
    dateProperties: this.dateProperties,
    utcDateProperties: this.options.utcDateProperties ?? null,

    toFirestore(data: WithFieldValue<Model>): DocumentData {
      if(this.utcDateProperties){
        this.utcDateProperties.forEach(property=>getPropertyUtcDate(data,property));
      }
      return data as DocumentData;
    },
    fromFirestore(
      snapshot: QueryDocumentSnapshot,
      options: SnapshotOptions,
    ): Model {
      const data = snapshot.data(options)!;
      if(this.utcDateProperties){
        this.utcDateProperties.forEach(property=>setPropertyUtcDate(data,property));
      }      
      this.dateProperties.forEach(property=>setPropertyDate(data,property));
      return data as Model;
    }
  };

  esNormalize = (item:any) => {
    if(this.options.utcDateProperties){
      this.options.utcDateProperties.forEach(property=>setUtcEsPropertyDate(item,property));
    }    
    this.dateProperties.forEach(property=>setEsPropertyDate(item,property));
    return item as Model;
  };

  constructor(
    protected firestore: Firestore,
    protected es: ElasticSearchService,
    protected collectionName:string,
    protected esExtras:ESExtras = null,
    protected esSource:Array<string> = null,
    protected dateProperties = ['createdAt', 'updatedAt'],
    protected options:EsFirestoreBaseOptions = {}

  ) {
    if (this.firestore){
      this.collection = collection(this.firestore, this.collectionName).withConverter(this.genericConverter);
    }
  }
  get$(id:string): Observable<Model> {
    const docRef = doc(this.collection, id);
    return docData(docRef, {idField:'id'}) as Observable<Model>;
  }

  getAll$():Observable<Model[]> {
    const _query = query(this.collection, limit(100)) // TODO: handle this limit properly
    return collectionData(_query, {idField:'id'}) as Observable<Model[]>;
  }

  upsert(_data, isNew=false): Promise<any> {
    const {...data } = _data;
    data.updatedAt = serverTimestamp();
    data._version = increment(1);
    if (!data.id){
      isNew = true;
      data.id = this.createId();
    }
    if(isNew) {
      data.createdAt = serverTimestamp()
    }
    const merge = !(isNew);
    const docRef:DocumentReference = doc(this.collection, data.id);
    return setDoc(docRef, data, {merge}).then(()=> data.id)
  }

  delete(id):Promise<any>{
    const docRef = doc(this.collection, id);
    return deleteDoc(docRef);
  }

  onChange(): Observable<any> {
    const docRef = doc(this.firestore,'refresh', this.collectionName);
    return docData(docRef) as Observable<any>;
  }

  createId() {
    return doc(this.collection).id;
  }

  query$(paginator, sort, filter, options?):Observable<any>{
    const extras = this.esExtras;
    const source = this.esSource;
    const index = this.collectionName;
    const body = this.es.mtToBody(paginator, sort, filter, extras, source);
    return this.es.query$(index , body, this.esNormalize);
  }

}

export const mapPrimeTable2Mat = (conditions)=>{
  let sort:ESSort = {active:null, direction:null};
  const {options} = conditions;
  //Check Sort
  if (options.sortField){
    sort.active = options.sortField;
    sort.direction = options.sortOrder==1
                     ? 'asc'
                     : options.sortOrder==-1
                        ? 'desc'
                        : null;
  }

  //Check Filter
  const filters = ngFilterToMatFilter(options.filters);
  //name: [{value: '01', matchMode: 'contains', operator: 'and']
  //const filters = {name: [{ key: 'name', value: name, op: '==' }]};

  //Check Paginator
  const paginator = ngPaginatorToMatPaginator(options);
  return {paginator, sort, filters} as any;
}

const ngPaginatorToMatPaginator = (options) => {
  return {pageIndex:options.first/options.rows, pageSize:options.rows }
}

const ngFilterToMatFilter = (ngFilter)=>{
  const filters = {};
  Object.keys(ngFilter).forEach(property => {
    const filter:any = ngFilter[property];
    if (filter.length){
      filters[property] = filter.filter(t=>t.value).map(f=>({key:property, value:f.value, op: '=='}));
    }
  });
  return filters;
}

const setPropertyDate = (data, key)=>{
  const value  = get(data, key, null);
  if (value){
   set(data, key, toDate(value))
  }
}

const setEsPropertyDate = (data, key)=>{
  const value  = get(data, key, null);
  if (value){
   set(data, key, new Date(value['_seconds'] * 1000))
  }
}


const getPropertyUtcDate = (data, key)=>{
  const value  = get(data, key, null);
  if (value){
   set(data, key, zonedTimeToUtc(value,"UTC"))
  }
}
const setPropertyUtcDate = (data, key)=>{
  const value  = get(data, key, null);
  if (value){
   set(data, key, utcToZonedTime(toDate(value),"UTC"))
  }
}
const setUtcEsPropertyDate = (data, key)=>{
  const value  = get(data, key, null);
  if (value){
   set(data, key, utcToZonedTime(new Date(value['_seconds'] * 1000),"UTC"));
  }
}